import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Github, ExternalLink, Code2 } from "lucide-react";

const projectList = [
  {
    name: "Finance Tracker",
    tech: ["React", "Node.js", "MongoDB", "Express", "JWT", "Tailwind CSS"],
    description: "A full-stack personal finance tracker with authentication (email/password & Google OAuth), dashboard analytics, transaction management, device/session management, profile editing, dark mode, and email notifications.",
    link: "https://github.com/nidhisoni01/finance-tracker",
    demo: null,
    category: "Full Stack",
    color: "from-blue-500 to-cyan-500",
  },
  {
    name: "AI-CODING-ASSISTANT",
    tech: ["HTML", "CSS", "JavaScript", "OpenAI API", "Chrome Extension"],
    description: "Developed a Chrome extension that integrates AI assistance into maang.in (AlgoZenith) for real-time problem-solving support. Implemented a responsive chat UI with light/dark mode, API key management, and seamless interaction using HTML, CSS, and JavaScript.",
    link: "https://github.com/nidhisoni01/AI-CODING-ASSISTANT",
    demo: null,
    category: "Extension",
    color: "from-purple-500 to-pink-500",
  },
  {
    name: "SLEEK-SPORTS-CARS",
    tech: ["HTML", "CSS", "JavaScript"],
    description: "Created a dynamic and visually appealing landing page for a sports car brand using HTML, CSS, and JavaScript. Implemented responsive layouts, interactive animations, and smooth scrolling. Focused on modern UI/UX practices.",
    link: "https://github.com/nidhisoni01/SLEEK-SPORTS-CARS",
    demo: "https://carshowcaseee.netlify.app/#",
    category: "Frontend",
    color: "from-orange-500 to-red-500",
  },
  {
    name: "Website Clones",
    tech: ["HTML", "CSS", "JavaScript", "React"],
    description: "Replicated popular website interfaces using HTML, CSS, and JavaScript to strengthen frontend skills and design precision. Focused on responsive layouts and interactive elements.",
    link: "#",
    demo: null,
    category: "Frontend",
    color: "from-green-500 to-emerald-500",
  },
];

const Projects = () => {
  const [filter, setFilter] = useState("All");

  const categories = ["All", ...new Set(projectList.map((p) => p.category))];

  const filteredProjects =
    filter === "All"
      ? projectList
      : projectList.filter((p) => p.category === filter);

  return (
    <section id="projects" className="min-h-screen py-20 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Projects</span>
          </h2>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full mb-8"></div>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Here are some of my recent projects that showcase my skills and passion for development
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${filter === category
                  ? "bg-gray-200 text-gray-900 shadow-lg scale-105 hover:bg-white"
                  : "glass text-gray-300 hover:text-white hover:scale-105"
                }`}
            >
              {category}
            </button>
          ))}

        </motion.div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {filteredProjects.map((proj, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative glass rounded-2xl p-8 overflow-hidden hover:scale-105 transition-transform duration-300"
            >
              {/* Background on Hover */}
              <div
                className="absolute inset-0 bg-gray-400 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
              />

              <div className="relative z-10">
                {/* Category Badge */}
                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-400 text-gray-900 mb-4">
                  {proj.category}
                </span>

                {/* Project Icon */}
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-300 flex items-center justify-center mb-4">
                    <Code2 className="w-6 h-6 text-gray-900" />
                  </div>
                </div>

                {/* Project Name */}
                <h3 className="text-2xl font-bold text-gray-200 mb-3 group-hover:text-gray-100 transition-all duration-300">
                  {proj.name}
                </h3>

                {/* Description */}
                <p className="text-gray-300 mb-6 leading-relaxed line-clamp-4">
                  {proj.description}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {proj.tech.map((stack, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-gray-400 text-gray-900 border border-gray-500"
                    >
                      {stack}
                    </span>
                  ))}
                </div>

                {/* Links */}
                <div className="flex items-center gap-4">
                  {proj.link && proj.link !== "#" && (
                    <motion.a
                      href={proj.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-300 hover:text-gray-100 transition-colors font-semibold"
                      whileHover={{ x: 5 }}
                    >
                      <Github className="w-5 h-5" />
                      Code
                    </motion.a>
                  )}
                  {proj.demo && (
                    <motion.a
                      href={proj.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-300 hover:text-gray-100 transition-colors font-semibold"
                      whileHover={{ x: 5 }}
                    >
                      <ExternalLink className="w-5 h-5" />
                      Live Demo
                    </motion.a>
                  )}
                  <motion.div
                    className="ml-auto"
                    whileHover={{ scale: 1.2, rotate: 45 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-gray-900" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
